import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';


import { ApplicationState, ProductSelectors } from '../reducers';
import { Destination } from '../../objects/destination';
import { Product } from '../../objects/product';
import { BaseActionService } from './base.action.service';
import { ProductActions } from './actions';
import { map } from 'rxjs/operators';
import { isNil } from 'lodash-es';
import { DateOfStay } from 'src/objects/date-of-stay';
import { Storefront } from './storefront.service';

/**
 * DateOfStay action service.
 * Purpose of it is to abstract communication with Store
 */
export interface IProductActionService {
  loadProduct(slugOrId: string | number): void;
  loadProductInventoryCount(product: Product, destination: Destination, dateOfStay: DateOfStay): void;
  loadProducts(destination: Destination, storefront: Storefront | null): void;
  loadProductsByIds(ids: number[]): void;

  setProduct(product: Product | null): void;

  getProduct(): Observable<Product | null>;
  getProducts(): Observable<Product[] | null>;
  getProductLoading(): Observable<boolean>;
  getProductsLoading(): Observable<boolean>;
  getProductError(): Observable<Error | null>;
}

@Injectable({
  providedIn: 'root'
})
export class ProductActionService
  extends BaseActionService<ApplicationState>
  implements IProductActionService {
  protected store: Store<ApplicationState>;


  constructor() {
    const store = inject<Store<ApplicationState>>(Store);

    super(store);

    this.store = store;
  }

  readonly product = this.selectSignal(ProductSelectors.getProduct);
  readonly products = this.selectSignal(ProductSelectors.getProducts);
  readonly productLoading = this.selectSignal(ProductSelectors.getProductLoading);
  readonly productsLoading = this.selectSignal(ProductSelectors.getProductsLoading);
  readonly productInventoryCountLoading = this.selectSignal(ProductSelectors.getProductInventoryCountLoading);
  readonly productError = this.selectSignal(ProductSelectors.getProductError);

  loadProductInventoryCount(product: Product, destination: Destination, dateOfStay: DateOfStay | null) {
    super.load(ProductActions.loadProductInventoryCount({ product, destination, dateOfStay }));
  }

  loadProduct(slugOrId: string | number) {
    super.load(ProductActions.loadProduct({ slugOrId }));
  }

  loadProducts(destination: Destination, storefront: Storefront | null) {
    super.load(ProductActions.loadProducts({ destination, storefront }));
  }

  loadProductsByIds(ids: number[]) {
    super.load(ProductActions.loadProductsByIds({ ids }));
  }

  setProduct(product: Product | null) {
    super.load(ProductActions.setProduct({ product }));
  }

  setProductQuantity(payload: {
    product: Product | null;
    quantity: number;
  }) {
    super.load(ProductActions.setProductQuantity(payload));
  }

  getProduct(): Observable<Product| null> {
    return super.get(ProductSelectors.getProduct);
  }

  getProducts(): Observable<Product[] | null> {
    return super.get(ProductSelectors.getProducts);
  }

  getProductQuantity(payload: { product: Product }): Observable<number | null> {
    const id = payload.product.id;

    return this.store.select(ProductSelectors.getProductQuantity)
      .pipe(
        map((byId: (productId: number) => number) => {
          const quantity = +byId(id);
          if (isNil(quantity) || isNaN(quantity)) {
            return null;
          }

          return quantity;
        }),
      );
  }

  getProductInventoryCount(payload: { product: Product; destination: Destination }): Observable< number | null > {
    const { product, destination } = payload;
    const isDelivery = destination?.destination_type === 'delivery' || (destination?.delivery_warehouse ?? false);
    const destinationId = isDelivery ? 'delivery' : destination.id;
    const productId = product.id;
    
    return this.store.select(ProductSelectors.getProductInventoryCount)
      .pipe(
        map((normalizedCount: { [destinationId: number | string]: { [productId: number]: number | null } }) => 
          normalizedCount?.[destinationId]?.[productId] ?? null
        )
      );
  }

  getProductLoading(): Observable<boolean> {
    return super.get(ProductSelectors.getProductLoading);
  }

  getProductsLoading(): Observable<boolean> {
    return super.get(ProductSelectors.getProductsLoading);
  }

  getProductInventoryCountLoading(): Observable<boolean> {
    return super.get(ProductSelectors.getProductInventoryCountLoading);
  }

  getProductError(): Observable<Error | null> {
    return super.get(ProductSelectors.getProductError);
  }
}
